import { useContext, useEffect, useState } from 'react'
import SectionContext from '../Context'
import themeConfig from '../../../../theme.config'
import { getRGB } from '../../../helpers/style'
import * as HeroIcons from '@heroicons/react/24/solid'

const DynamicHeroIcon = (props) => {
  const { ...icons } = HeroIcons
  let TheIcon
  if (props.link?.linkIconClass) {
    TheIcon = icons[`${props.link?.linkIconClass}Icon`]
  } else if (props.icon) {
    TheIcon = icons[`${props.icon}Icon`]
  }

  let isIconvisibleClass = ''
  if (props.fn && (props.fn === 'utility' || props.fn === 'contact')) {
    isIconvisibleClass = 'xl:hidden 2xl:inline-block'
  }

  return (
    <TheIcon
      className={`h-6 mr-4 shrink-0 w-6 text-current mr-1 ${props.iconClass ? props.iconClass : ''} ${isIconvisibleClass}`}
      role="none"
      aria-hidden="true"
    />
  )
}

const getBorderTheme = (skip = false) => (skip ? `border border-light` : '')

const setButtonShades = (theme, shade, hoverShade) => {
  return `bg-${theme}-${shade} hover:bg-${theme}-${hoverShade} focus-visible:outline-${theme}-${shade} text-${theme}-${shade}-text hover:text-${theme}-${hoverShade}-text`
}

const getButtonRGB = (theme, shade) => Object.values(getRGB(themeConfig.colors.shades[theme][shade])).join(',')

const overrideButton = (theme, buttonShade, themeClasses, base) => {
  const buttonRGB = {
    primary: getButtonRGB('primary', buttonShade),
    secondary: getButtonRGB('secondary', buttonShade),
  }

  if (Object.values(getRGB(base)).join(',') === buttonRGB[theme]) {
    return themeConfig.settings.useWhiteButtonsOnCollision
      ? `bg-dark text-light ${getBorderTheme(true)}`
      : `${setButtonShades(theme, themeConfig.settings.buttonShades.muted.base, themeConfig.settings.buttonShades.muted.hover)} ${getBorderTheme(buttonRGB[theme] === Object.values(getRGB('#ffffff')).join(','))}`
  }
  return themeClasses
}

function ButtonEl(props) {
  let buttonShade = themeConfig.settings.buttonShades.standard.base
  let buttonHoverShade = themeConfig.settings.buttonShades.standard.hover
  const type = ['default', 'rounded', 'circle'].includes(props.buttonType) ? props.buttonType : 'default'
  let theme = ['primary', 'secondary', 'tertiary', 'text', 'none'].includes(props.theme) ? props.theme : 'secondary'
  // we do it this long-ass way because I did not want to go and add a flag to every button call across the site.
  const showDecorator = props.showDecorator === false ? false : true

  theme = theme === 'text' ? 'none' : theme

  const { base } = useContext(SectionContext)
  const [baseColor, setBaseColor] = useState('#fff')
  const [themeClasses, setThemeClasses] = useState(setButtonShades(theme, buttonShade, buttonHoverShade))
  const [hoverThemeClasses, setHoverThemeClasses] = useState(`group-hover:bg-${theme}-${buttonHoverShade}-text`)
  const [aria, setAria] = useState({})

  let typeClasses = type === 'default' ? 'rounded' : 'rounded-full'
  // if the config is set to not use square buttons, override the class
  themeConfig.settings.useOnlyPillButtons && (typeClasses = 'rounded-full')

  useEffect(() => {
    let theBase = base
    if (props.overrideBase) {
      const str = props.overrideBase.split('-')
      theBase = themeConfig.colors.shades[str[0]][str[1]]
    }
    setBaseColor(theBase)
  }, [base])

  useEffect(() => {
    setAria((prevState) => {
      let newState = { ...prevState }
      if (!props.ariaHidden || props.ariaHidden === 'false') {
        delete newState['aria-hidden']
        delete newState.tabIndex
      } else {
        newState['aria-hidden'] = 'true'
        newState.tabIndex = -1
      }
      if (props.ariaHasPopup) {
        newState['aria-haspopup'] = props.ariaHasPopup
      }
      if (props.ariaControls) {
        newState['aria-controls'] = props.ariaControls
      } else {
        delete newState['aria-controls']
      }
      if (props.ariaExpanded) {
        newState['aria-expanded'] = props.ariaExpanded
      } else {
        delete newState['aria-expanded']
      }
      return newState
    })
  }, [props.ariaHidden, props.ariaControls, props.ariaExpanded])

  useEffect(() => {
    setThemeClasses(overrideButton(theme, buttonShade, themeClasses, baseColor))
    setHoverThemeClasses(`group-hover:bg-${theme}-${buttonHoverShade}-text`)
  }, [baseColor])

  const className = `cta group font-semibold ${theme !== 'none' ? 'shadow-sm text-shadow-off px-5  ' : ''} py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 flex transition-all duration-300 ${themeClasses} ${typeClasses}  ${props.className ? props.className : ''}`

  const getLabel = (props) => {
    return props.label || props.children || props.link?.name || props.link?.label
  }

  const inner = (
    <div className="flex flex-wrap items-center w-full">
      {/* {theme !== 'none' && ( */}
      <>
        <div className="cta-content flex flex-nowrap items-center justify-center w-full">
          {props.icon && <DynamicHeroIcon {...props} />}
          {getLabel(props)}
        </div>
        <span
          data-type="button-decorator"
          className={`block basis-full max-w-[1px] bg-transparent group-hover:max-w-full transition-all duration-500 h-0.5 ${theme === 'none' ? 'group-hover:bg-[currentColor]' : hoverThemeClasses}`}
        />
      </>
    </div>
  )

  const pushEngageEvent = (e, props) => {
    let target = e.target
    if (!["BUTTON", "A"].includes(target.nodeName)) {
      target = target.closest("a", "button")
    }
    umbEngage(
      "send",
      "event",
      `${theme.toString().trim()} CTA`,
      "CTA Click",
      target.innerText.trim()
    )
    if (props.onClick) props.onClick()
  }

  return props.link ? (
    <a
      href={props.link.url}
      target={props.link.target}
      className={`${className}`}
      onClick={props.overrideEngagePush ? props.onClick : (e) => {
        pushEngageEvent(e, props);
      }}
      // onClick={props.onClick}
      tabIndex={props.active === undefined || props.active === 'true' ? 0 : -1}
      {...aria}
    >
      {inner}
    </a>
  ) : (
    <button
      type={props.type ?? 'button'}
      className={`${className}`} // had to wrap in {``} to get passed in className to be accepted.
      // onClick={(e) => {
      //   pushEngageEvent(e, props)
      //   if (props.onClick) props.onClick
      // }}
      onClick={props.onClick}
      {...aria}
      data-idx={props.idx || '0'}
    >
      {inner}
    </button>
  )
}

export default ButtonEl
